const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/avif', 'image/webp'];
const MAX_DIMENSION = 1200;
const WEBP_QUALITY = 0.70;

/**
 * Re-encode to WebP, scaling down only if either side exceeds `maxDimension`.
 *
 * @param maxDimension defaults to 1200, which suits menu photos. Pass a larger cap for images that are
 *                     meant to stay at their given size — the hero banner is 1280 wide, so the default
 *                     would silently shrink it to 1200x375 and it would no longer be the size the
 *                     merchant was asked to supply.
 */
function compressImageToWebp(file, maxDimension = MAX_DIMENSION) {
    return new Promise((resolve, reject) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            reject(new Error('Unsupported file type: ' + file.type));
            return;
        }

        // If already webp and small enough (< 100KB), skip compression
        if (file.type === 'image/webp' && file.size < 100 * 1024) {
            resolve(file);
            return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            let width = img.width;
            let height = img.height;

            // Scale down if larger than maxDimension
            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    height = Math.round(height * (maxDimension / width));
                    width = maxDimension;
                } else {
                    width = Math.round(width * (maxDimension / height));
                    height = maxDimension;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Failed to compress image'));
                        return;
                    }
                    const originalName = file.name.replace(/\.[^.]+$/, '');
                    const webpFile = new File([blob], originalName + '.webp', {
                        type: 'image/webp'
                    });
                    console.log(`Image compressed: ${file.name} (${(file.size / 1024).toFixed(1)}KB) -> ${webpFile.name} (${(webpFile.size / 1024).toFixed(1)}KB)`);
                    resolve(webpFile);
                },
                'image/webp',
                WEBP_QUALITY
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };

        img.src = url;
    });
}

/**
 * What would happen to an image of this size in this slot — decided BEFORE anything is uploaded.
 *
 * Exported because the merchant is asked about it first, and the answer they are given has to be the
 * answer the crop actually produces. Two separate calculations would eventually disagree, and the one
 * they would notice is the warning that promised a different picture.
 *
 * @returns {{sx,sy,cw,ch,dw,dh, tooSmall: boolean, cropFraction: number}}
 *          `cropFraction` is how much of the source area is discarded, 0 when nothing is.
 */
function plan(sw, sh, slot) {
    let sx = 0, sy = 0, cw = sw, ch = sh, dw, dh;

    if (slot.fit === 'contain') {
        const scale = Math.min(slot.width / sw, slot.height / sh, 1);   // never above 1: no upscaling
        dw = Math.round(sw * scale);
        dh = Math.round(sh * scale);
    } else {
        // Centre-crop the source to the slot's aspect ratio, then scale that down to fit.
        const target = slot.width / slot.height;
        if (sw / sh > target) {
            cw = Math.round(sh * target);
            sx = Math.round((sw - cw) / 2);
        } else {
            ch = Math.round(sw / target);
            sy = Math.round((sh - ch) / 2);
        }
        const scale = Math.min(slot.width / cw, 1);
        dw = Math.round(cw * scale);
        dh = Math.round(ch * scale);
    }

    const min = slot.min || { width: slot.width, height: slot.height };
    return {
        sx: sx, sy: sy, cw: cw, ch: ch, dw: dw, dh: dh,
        tooSmall: sw < min.width || sh < min.height,
        cropFraction: 1 - (cw * ch) / (sw * sh)
    };
}

/**
 * The image's pixel dimensions, without touching the file. Read before the upload so a picture that
 * will be refused never travels, and so the merchant is asked about a crop before it happens.
 */
function readDimensions(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };
        img.src = url;
    });
}

/**
 * Re-encode to WebP, shaped for the slot it is going into.
 *
 * `cover` crops to the slot's proportions from the CENTRE and scales to its size; `contain` scales to
 * fit inside them without cropping, for logos. Neither ever upscales — a small photo comes out small
 * and correctly shaped, and the caller is told so through `tooSmall`.
 *
 * Centre is the right default crop and not a perfect one: a dish photographed low in the frame loses
 * its top. Letting the merchant drag the focal point is the next step, not a reason to leave every
 * upload to the browser's own crop, which is centre too and happens without telling anyone.
 *
 * @param slot {{width:number, height:number, fit:string}} from SiteImageSlots
 * @returns {Promise<{file: File, width: number, height: number}>}
 */
function compressImageToSlot(file, slot) {
    return new Promise((resolve, reject) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            reject(new Error('Unsupported file type: ' + file.type));
            return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);
            const sw = img.width;
            const sh = img.height;
            if (!sw || !sh) {
                reject(new Error('Failed to read image dimensions'));
                return;
            }

            // The same geometry the merchant was shown before they agreed to it.
            const { sx, sy, cw, ch, dw, dh } = plan(sw, sh, slot);

            const canvas = document.createElement('canvas');
            canvas.width = dw;
            canvas.height = dh;
            const ctx = canvas.getContext('2d');
            // Left transparent rather than filled: a logo's transparency has to survive, and WebP keeps it.
            ctx.drawImage(img, sx, sy, cw, ch, 0, 0, dw, dh);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Failed to compress image'));
                        return;
                    }
                    const originalName = file.name.replace(/\.[^.]+$/, '');
                    const webpFile = new File([blob], originalName + '.webp', { type: 'image/webp' });
                    console.log(`Image fitted: ${file.name} (${sw}x${sh}, ${(file.size / 1024).toFixed(1)}KB)`
                        + ` -> ${dw}x${dh}, ${(webpFile.size / 1024).toFixed(1)}KB`);
                    resolve({ file: webpFile, width: dw, height: dh });
                },
                'image/webp',
                WEBP_QUALITY
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };

        img.src = url;
    });
}

export default { compressImageToWebp, compressImageToSlot, plan, readDimensions };
